import { Response } from 'datx-jsonapi';
import { FC, useMemo, useState } from 'react';

import { FilterCombobox } from '@/components/ui/features/library/LibraryFilter/components/FilterCombobox';
import { useLibraryFilters } from '@/components/ui/features/library/LibraryFilter/LibraryFilter.hooks';
import { useAnalytics } from '@/context/Analytics';
import { LibraryFilter } from '@/enums/LibraryFilter';
import { useLibLynx } from '@/hooks/useLibLynx';
import { useQueryParams } from '@/hooks/useQueryParams';
import { IMetaOmnisearchProps, IOmnisearchFilterItem } from '@/interfaces/search';
import { useResource, useResourceRelationship, useResourcesInfinite } from '@/lib/datx-jsonapi-react';
import { getHubBySlug, getHubsSubtree } from '@/queries/hubs';
import {
  queryOmnisearchThinLectureByFilterInfinite,
  queryOmnisearchThinLectureFiltersRelationship,
} from '@/queries/omnisearch-thin-lectures';
import { OmnisearchFilterItem } from '@/resources/OmnisearchFilterItem';
import { extractSWRInfiniteData } from '@/utils/swr/extractSWRInfiniteData';
import { StringParam } from '@/utils/url-query-string';

const defaultSubject = { label: 'All subjects', value: undefined, amount: null };

function formatSubjectFilterItemToComboboxItem(subject?: IOmnisearchFilterItem) {
  if (!subject) return null;
  const subjectCategoriesArray = subject.label?.split('/');
  const lastSubject = subjectCategoriesArray?.pop();

  return {
    title: lastSubject,
    subTitle: subjectCategoriesArray.join('/'),
    value: subject.value,
    amount: subject.amount,
  };
}

export const FilterSubjects: FC = () => {
  const analytics = useAnalytics();
  const [subjectSearch, setSubjectSearch] = useState('');
  const libLynx = useLibLynx();

  // get value of currently selected conference
  const { filters, change } = useLibraryFilters({
    [LibraryFilter.Subject]: StringParam,
  });

  const { query, slug } = useQueryParams<{ query: string; slug: string }>({
    query: StringParam,
    slug: StringParam,
  });

  const { data: hubsData } = useResource(getHubBySlug(slug));
  const {
    data: { subtreeIds },
  } = useResourceRelationship(getHubsSubtree(hubsData.id));

  const { data: resultsData } = useResource(
    queryOmnisearchThinLectureFiltersRelationship({
      library: 'true',
      scope: 'published',
      query,
      ...filters,
    })
  );

  const { data, error, size, setSize } = useResourcesInfinite(
    queryOmnisearchThinLectureByFilterInfinite('subjects', {
      filters: {
        library: 'true',
        scope: 'published',
        subjects_query: subjectSearch,
        subject_id: subtreeIds,
        query,
        ...filters,
      },
    }),
    {
      revalidateAll: false,
    }
  );

  const { items, isLoadingMore } = useMemo(
    () => extractSWRInfiniteData<OmnisearchFilterItem>(data as Array<Response<OmnisearchFilterItem>>, error, size),
    [data, error, size]
  );

  const isReachingEnd = useMemo(() => {
    if (!data) return false;
    const lastPage = data?.[data.length - 1].meta as IMetaOmnisearchProps;

    return lastPage.currentPage >= lastPage.totalPages;
  }, [data]);

  const onChange = (item) => {
    change({ [LibraryFilter.Subject]: item?.value }, { method: 'replace', shallow: true, scroll: true });
    analytics.track('library_subject_filter', { subject_name: item?.title, subject_id: item?.value });
    libLynx.send('trackSearch');
  };

  return (
    <FilterCombobox
      getItem={formatSubjectFilterItemToComboboxItem}
      placeholder="Search"
      itemsList={[defaultSubject, ...items]}
      defaultItem={resultsData?.selectedSubject || defaultSubject}
      onChangeItem={(item) => onChange(item)}
      isLoadingMore={isLoadingMore && !isReachingEnd}
      onScrollToBottom={() => {
        if (!error && !isReachingEnd) {
          setSize(size + 1);
        }
      }}
      onSearchChange={(value) => setSubjectSearch(value)}
    />
  );
};
